<template>
    <DsSheet
        v-model:open="open"
        size="sm"
        title="Payoff statement details"
    >
        <div class="flex-1 rounded-b-3xl bg-white">
            <!-- Loading state -->
            <div
                v-if="isLoadingDetails"
                class="space-y-4"
            >
                <div class="animate-pulse">
                    <div class="mb-2 h-4 w-3/4 rounded bg-ds-primary-200"></div>
                    <div class="h-4 w-1/2 rounded bg-ds-primary-200"></div>
                </div>
            </div>

            <!-- Error state -->
            <PayoffOrderDetailsError
                v-else-if="detailsError"
                @retry="fetchDetails"
            />

            <!-- Success state -->
            <div
                v-else-if="payoff"
                class="space-y-6"
            >
                <div class="space-y-2">
                    <div class="mb-2 flex items-center gap-2">
                        <PayoffOrderStatus :status="payoff.status" />
                    </div>
                    <h1 class="text-2xl font-bold text-content-primary">{{ payoff.borrower_name }}</h1>
                    <div class="flex items-center gap-2 text-content-secondary">
                        <DsIcon
                            icon="fa-regular fa-house"
                            size="sm"
                        />
                        <span class="text-sm">{{ payoff.property_address }}</span>
                    </div>
                    <div class="mt-2 flex items-center gap-3">
                        <PayoffVerifiedStatusBadge :isVerified="payoff.beneficiary_information.verified" />
                        <PayoffInsuranceStatusBadge :isInsured="payoff.beneficiary_information.insured" />
                    </div>
                </div>

                <DsAlertBanner
                    v-if="payoff.has_message && payoff.message_type !== null"
                    title="Unable to verify payoff"
                    :variant="payoff.message_type"
                >
                    {{ payoff.message }}
                </DsAlertBanner>

                <dl class="my-4 grid grid-cols-2 gap-4">
                    <dt class="section-header col-span-2">Loan information</dt>
                    <dt class="field-label">Payoff amount</dt>
                    <dd class="field-value">
                        {{ payoffAmountDisplay }}
                    </dd>
                    <dt class="field-label">Good through date</dt>
                    <dd
                        v-if="payoff.loan_information.good_through_date"
                        class="field-value"
                    >
                        {{ formatDate(payoff.loan_information.good_through_date, 'MMMM D, YYYY') }}
                    </dd>
                    <dt class="field-label">Per diem</dt>
                    <dd class="field-value">{{ perDiemDisplay }}</dd>
                </dl>
                <hr class="border-t border-ds-primary-200" />
                <dl class="grid grid-cols-2 gap-4">
                    <dt class="section-header col-span-2">Beneficiary information</dt>
                    <dt class="field-label">Lender</dt>
                    <dd class="field-value">{{ payoff.beneficiary_information.lender_name }}</dd>
                    <dt class="field-label max-w-24">Address</dt>
                    <dd class="field-value">{{ lenderAddressDisplay }}</dd>
                    <dt class="field-label">Routing number</dt>
                    <dd class="field-value">{{ routingNumberDisplay }}</dd>
                    <dt class="field-label">Account number</dt>
                    <dd class="field-value">{{ accountNumberDisplay }}</dd>
                </dl>
                <hr class="border-t border-ds-primary-200" />
                <PayoffOrderDocumentList :documents="payoff.documents" />
            </div>
        </div>
    </DsSheet>
</template>

<script setup lang="ts">
import PayoffInsuranceStatusBadge from '@/components/Components/Payoff/PayoffInsuranceStatusBadge.vue';
import PayoffOrderDetailsError from '@/components/Components/Payoff/PayoffOrderDetailsError.vue';
import PayoffOrderDocumentList from '@/components/Components/Payoff/PayoffOrderDocumentList.vue';
import PayoffOrderStatus from '@/components/Components/Payoff/PayoffOrderStatus.vue';
import PayoffVerifiedStatusBadge from '@/components/Components/Payoff/PayoffVerifiedStatusBadge.vue';
import { EMPTY_CELL_CONTENT } from '@/components/Components/static-content';
import DsAlertBanner from '@ds/alert-banner/DsAlertBanner.vue';
import DsIcon from '@/components/design-system/DsIcon.vue';
import DsSheet from '@/components/design-system/DsSheet.vue';
import { formatDate } from '@/shared/utils/format-date';
import { formatCentsAsMoney } from '@/shared/utils/format-number';
import { usePayoffOrderStore } from '@/stores/company/payoff-order.store';
import { computed } from 'vue';

const open = defineModel<boolean>('open', { default: false });

const payoffOrderStore = usePayoffOrderStore();
const { payoff, isLoadingDetails, detailsError, fetchDetails } = payoffOrderStore.fetchPayoffDetails();

// note: explicit null check is intentional for the following properties
const payoffAmountDisplay = computed(() => {
    return payoff.value && payoff.value.loan_information.payoff_amount !== null
        ? formatCentsAsMoney(payoff.value.loan_information.payoff_amount)
        : EMPTY_CELL_CONTENT;
});

const perDiemDisplay = computed(() => {
    return payoff.value && payoff.value.loan_information.per_diem !== null
        ? `${formatCentsAsMoney(payoff.value.loan_information.per_diem)} per day`
        : EMPTY_CELL_CONTENT;
});

// note: we are checking against an empty string, not strictly null / undefined, so || is intentional for the following properties
const lenderAddressDisplay = computed(() => {
    return payoff.value?.beneficiary_information.lender_address || EMPTY_CELL_CONTENT;
});

const routingNumberDisplay = computed(() => {
    return payoff.value?.beneficiary_information.routing_number || EMPTY_CELL_CONTENT;
});

const accountNumberDisplay = computed(() => {
    return payoff.value?.beneficiary_information.account_number || EMPTY_CELL_CONTENT;
});
</script>

<style scoped lang="scss">
.section-header {
    @apply text-xs font-semibold text-content-primary;
}

.field-label {
    @apply text-sm font-normal leading-5 text-content-secondary;
}
.field-value {
    @apply text-right text-sm font-bold text-content-primary;
}
</style>
